/**
 * State-group CONTEXT resolution and the states session — the headless core of
 * the editor's Selection/Context split (STATE_GROUP_BOUNDARIES.md "Phase
 * 3.5"), so any host editor (sandbox strip, TraceFrontend states panel) can run
 * one implementation, and so its semantics are unit-testable without a host UI.
 *
 * Two concepts, kept strictly apart:
 *  - The SELECTION (hierarchy-owned) is the edit subject.
 *  - The CONTEXT is the group being orchestrated — derived, never set:
 *        context = resolveContext(selection)
 *
 * This module owns:
 *  - `resolveContext` — the boundary-aware derivation;
 *  - `markOpenedContext` / `isOpenedContext` — session navigation state;
 *  - `buildContextBreadcrumb` — the hierarchy-path breadcrumb around a
 *    selection (up-chain, siblings, and children for descend);
 *  - `createStatesSession` — one states editor: the derived context, the state
 *    each context is meant to show (with the DEMOTED-CONTEXT sweep: a lazy rig
 *    promotion can strip an object of its context role mid-session; its stale
 *    intent must migrate to the heir), the strip's operations, and close.
 */

import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { Properties } from '../../behaviors/properties';
import { Pages } from '../../behaviors/pages';
import type { Refusal } from '../authoring/types';
import { PLAY_MODE_REFUSAL } from '../authoring/types';
import { EDITOR_EVENTS, dispatchBehaviorChanged, type BehaviorChangedDetail } from '../undo/editor-events';
import {
    addStateForSubtree,
    applyStateToSubtree,
    cloneStateForSubtree,
    createNestedStateGroup,
    deleteStateForSubtree,
    dissolveNestedStateGroup,
    nearestStrictAncestorOwner,
    renameStateForSubtree,
    stateEditRefused,
    stateScopeOwner,
    switchState,
} from './statesActions';
import { groupDescendants, isGroupBoundary } from './groupBoundaries';

/** The Properties currently on the context, if any (created on first add). */
function contextProps(obj: SceneObject): Properties | null {
    return obj.getBehavior(Properties);
}

// --- Context resolution ------------------------------------------------------

/**
 * Session-only set of objects the user has explicitly OPENED as a states context
 * (by turning on States Editing for them, or by adding a child under them). This
 * is pure editor navigation state — a WeakSet, never a persisted behavior — so
 * it never reaches the scene graph or an export. It distinguishes "I opened this
 * object to author states" from "this mesh just happens to be selected while the
 * mode is globally on", which is what decides whether new assets nest under it.
 */
const openedContexts = new WeakSet<SceneObject>();

/** Record that the user explicitly opened `obj` as a states context. */
export function markOpenedContext(obj: SceneObject): void {
    openedContexts.add(obj);
}

/**
 * True when `obj` is a real states context — one that resolution rule 2 should
 * return for a descendant. That means it actually owns states, is a group
 * boundary, or was EXPLICITLY opened by the user.
 *
 * A bare, empty Properties behavior does NOT qualify: an object can carry
 * Properties purely for property *declarations* (no states, not a state group),
 * and pulling every descendant up to such an ancestor is the "resolves to the
 * parent" bug. Context is about STATES, not the mere presence of a Properties
 * behavior.
 */
export function isOpenedContext(obj: SceneObject): boolean {
    const p = contextProps(obj);
    if (p && (p.getStateNames().length > 0 || isGroupBoundary(obj))) return true;
    return openedContexts.has(obj);
}

/**
 * Resolve the default CONTEXT for a selection — pure and selection-driven:
 *
 *  0. If the selection ITSELF is a group boundary (owns an independent group), it
 *     is its own context — its states are not the outer group's, so never resolve
 *     up past it. (For a selection INSIDE a nested boundary, rule 1 already stops
 *     at that boundary: the owner walk — `nearestStrictAncestorOwner` /
 *     `stateScopeOwner` — is boundary-aware.)
 *  1. If the selection lives inside an existing state GROUP (a STRICT ancestor has
 *     Properties WITH STATES), that group is the context — selecting a member
 *     orchestrates the GROUP, per the editor convention.
 *  2. Else, the nearest self-or-ancestor that is an OPENED context (owns states,
 *     is a boundary, or was explicitly opened) — so any descendant of an opened
 *     object resolves back to it CONSISTENTLY.
 *  3. Else the SELECTION ITSELF (a standalone object with no states above is its
 *     own default context until it gains states or joins a group).
 *
 * Because this is a pure function of the selection + scene data, it is naturally
 * stable while the user clicks around inside a group (rules 1–2 keep resolving to
 * the same owner) — no stickiness machinery needed. To view a different group,
 * the user SELECTS it (breadcrumb or explorer); there is no separate view pin.
 */
export function resolveContext(selected: SceneObject): SceneObject | null {
    const root = selected.traceScene.getSceneRoot();
    if (selected === root) return null;

    // (0) The selection owns its own independent group → it is the context.
    if (isGroupBoundary(selected)) return selected;

    // (1) A real state group ABOVE me (strict ancestor with states) → the group's
    // OWNER, via the shared strict walk (see nearestStrictAncestorOwner /
    // stateScopeOwner): the panel context and the add/target routing answer this
    // question through one implementation. Strict so a seeded member isn't
    // mistaken for its own group; owner-resolved (not nearest-stateful) so a
    // switch from inside a member's subtree fans out over the WHOLE group;
    // boundary-aware so a nested independent group shadows any outer one.
    const ancestorGroup = nearestStrictAncestorOwner(selected);
    if (ancestorGroup) return ancestorGroup.sceneObject;

    // (2) Nearest self-or-ancestor that is an OPENED context — it owns states, is
    // a boundary, or the user explicitly opened it (openedContexts). So once you
    // open object X, its descendants resolve back to X even before X has states.
    let cur: SceneObject | null = selected;
    while (cur && cur !== root) {
        if (isOpenedContext(cur)) return cur;
        cur = cur.getParent();
    }

    // (3) No context above → the selection itself is the default context.
    return selected;
}

// --- Breadcrumb --------------------------------------------------------------

/**
 * Optional pin toggle on a breadcrumb menu entry (hosts decide the meaning —
 * the Pages strip uses it as the shared-across-pages thumbtack). `on` renders
 * the pin filled; clicking calls `onToggle` (the host performs the — undoable —
 * action and the breadcrumb rebuilds from data).
 */
export interface CrumbEntryPin {
    on: boolean;
    title?: string;
    onToggle: () => void;
}

/** One entry in a crumb's siblings/children hop menus. */
export interface CrumbEntry {
    object: SceneObject;
    label: string;
    pin?: CrumbEntryPin;
}

/** One segment of the context breadcrumb (see buildContextBreadcrumb). */
export interface ContextCrumb {
    object: SceneObject;
    label: string;
    /** The crumb whose state group the host's cards show. */
    isContext: boolean;
    /** The current hierarchy selection — the edit subject. */
    isSelection: boolean;
    /** Siblings at this level (excluding the crumb itself) for a lateral hop. */
    siblings: CrumbEntry[];
    /**
     * Children to descend into — filled on the SELECTION leaf only (intermediate
     * crumbs' children are the next crumb plus its siblings, already shown).
     */
    children: CrumbEntry[];
}

/**
 * The OWNER of the outer group above `obj` — the group `obj`'s subtree sits
 * inside, if any. `obj` may itself be a boundary (a nested group); we want the
 * group ABOVE it, so walk from its parent. Owner-resolved (stateScopeOwner)
 * like resolveContext rule 1, so the breadcrumb's enclosing chain hops
 * owner-to-owner instead of member-to-member.
 */
function outerGroupOwner(obj: SceneObject): SceneObject | null {
    const parent = obj.getParent();
    if (!parent) return null;
    const outer = stateScopeOwner(parent);
    return outer?.sceneObject ?? null;
}

/**
 * The BREADCRUMB around a selection: the full hierarchy path from the outermost
 * enclosing group owner (so nested groups show their enclosing chain) down to
 * the SELECTION. The context and the selection are both crumbs on this path
 * (the derivation guarantees the selection is self-or-descendant of the
 * context, and the context self-or-descendant of the outermost group owner).
 * Each crumb carries its siblings for lateral hops; the selection leaf also
 * carries its children for descending.
 */
export function buildContextBreadcrumb(context: SceneObject, subject: SceneObject): ContextCrumb[] {
    const root = context.traceScene.getSceneRoot();
    // Top of the path: the outermost group owner above the context, else the
    // context itself.
    let top = context;
    for (let outer = outerGroupOwner(top); outer; outer = outerGroupOwner(top)) top = outer;
    // Walk subject → up, collecting until `top` (inclusive). Falls back to the
    // full path below the root if the chain is ever broken (should not happen).
    const path: SceneObject[] = [];
    let cur: SceneObject | null = subject;
    while (cur && cur !== root) {
        path.unshift(cur);
        if (cur === top) break;
        cur = cur.getParent();
    }
    return path.map((o) => ({
        object: o,
        label: o.name,
        isContext: o === context,
        isSelection: o === subject,
        siblings: (o.getParent()?.getChildren() ?? [])
            .filter((s) => s !== o)
            .map((s) => ({ object: s, label: s.name })),
        children: o === subject
            ? o.getChildren().map((c) => ({ object: c, label: c.name }))
            : [],
    }));
}

// --- The states session ------------------------------------------------------

/** A session operation's outcome: `true` when it did something, `null` when there was nothing to do, or why it was refused. */
type SessionResult = true | null | { refused: Refusal };

/**
 * One states editor over a scene: the context its selection derives, the state
 * the editor means each context to show, and the strip's operations. A host
 * renders the strip from it and calls its operations; the session decides the
 * owner, the undo grouping and what closing leaves showing.
 *
 * Each data operation is one undo entry. While the scene is in Play with no
 * host guard configured (`configureStateActions`), data operations return
 * `{ refused }` and change nothing.
 */
export interface StatesSession {
    /** Follow a new selection (the edit subject); the context is derived from it. */
    update(selection: SceneObject | null): void;
    /** The context the selection derives (`resolveContext`), or null. */
    getContext(): SceneObject | null;
    /**
     * The state the strip reports for the context: the one the editor
     * selected, else the one the context's resolver shows; null is Base. Never
     * a state the context no longer has. The selection outlives Play, which
     * may leave the scene showing another state; the host restores it.
     */
    active(): string | null;
    /**
     * Show `name` (null = Base) on the context's group as editor picks
     * (`Properties.setEditorState`), playing each state's transition. Base
     * picks Base on the context and clears the members' picks, so a member
     * shows what its runtime slot holds, such as a showing page's look; a page
     * that poses the context itself yields to the Base the user picked. It is
     * a preview, not an edit, so Play doesn't refuse it.
     */
    select(name: string | null): void;
    /** Add a state to the context's group and show it. */
    add(): Promise<SessionResult>;
    /** Rename a state across the context's group; the selected state follows the rename. */
    rename(from: string, to: string): Promise<SessionResult>;
    /** Delete a state across the context's group; when it was selected, Base is selected. */
    remove(name: string): Promise<SessionResult>;
    /** Copy a state across the context's group and show the copy. */
    duplicate(name: string): Promise<SessionResult>;
    /** Give the SELECTION its own independent state group. */
    separate(): Promise<SessionResult>;
    /** Return the SELECTION's independent group to the group it sits in. */
    rejoin(): Promise<SessionResult>;
    /**
     * End the editing session. Every editor pick in the scene clears, instantly
     * — the strip's, and any other editor writer's (`applyStateToSubtree`, the
     * AI's group pose, thumbnails) — so each object shows its runtime slot
     * again: a showing page's look, else Base. Returns `true`. In Play the scene is left to Play and the
     * result is `{ refused }`. Either way the session's own selections are
     * dropped. Unlike the data operations, it never runs a host
     * `mutationGuard`: closing the editor doesn't leave Play. `onChange` is not
     * called; the context is unchanged.
     */
    close(): true | { refused: Refusal };
    /** Stop listening to the scene. */
    dispose(): void;
}

export interface StatesSessionOptions {
    /**
     * Called after the session re-derives its context: on `update`, after a
     * data operation, and when Properties data or the hierarchy changes. The
     * Properties signal is the editors' window-wide `BEHAVIOR_CHANGED`, so a
     * change in another scene on the page also calls it.
     */
    onChange?: () => void;
}

/**
 * Give the objects showing pages pose their look back after a rename or
 * delete dropped it: each deck re-applies its showing page's assignments itself
 * (`Pages.reapplyPageStates`), limited to `scope` when given.
 */
function reposePageLooks(scene: TraceScene, scope?: ReadonlySet<SceneObject>): void {
    for (const obj of scene.getSceneObjects()) {
        obj.getBehavior(Pages)?.reapplyPageStates({ scope });
    }
}

/** Tell panels what `obj` shows changed, so they re-read state-bound values (as `switchState` does). */
function notifyStateShown(obj: SceneObject): void {
    dispatchBehaviorChanged({ objectPath: obj.getPath(), behaviorType: 'Renderer', action: 'updated' });
}

function stateNames(obj: SceneObject): string[] {
    return contextProps(obj)?.getStateNames() ?? [];
}

export function createStatesSession(scene: TraceScene, options: StatesSessionOptions = {}): StatesSession {
    // The state the editor selected, PER CONTEXT (absent = Base): independent
    // nested groups each hold their own simultaneously.
    const intents = new Map<SceneObject, string>();
    let selection: SceneObject | null = null;
    let context: SceneObject | null = null;
    let disposed = false;

    const getIntent = (ctx: SceneObject): string | null => intents.get(ctx) ?? null;

    function setIntent(ctx: SceneObject, name: string | null): void {
        if (disposed) return; // an operation that finishes after dispose() records nothing
        if (name === null) intents.delete(ctx);
        else intents.set(ctx, name);
    }

    /**
     * Drop or migrate intents that no longer hold. A context that no longer
     * derives as its own (a lazy rig promotion made it a member) hands its
     * intent to the heir when the heir carries that state, and drops its own. A
     * context that lost its intended state (deleted, renamed outside the
     * session, undone) falls back to Base.
     */
    function sweep(): void {
        for (const ctx of [...intents.keys()]) {
            const heir = ctx.transform.isDisposed() ? null : resolveContext(ctx);
            const intent = intents.get(ctx);
            if (heir === ctx) {
                if (intent !== undefined && !stateNames(ctx).includes(intent)) intents.delete(ctx);
                continue;
            }
            if (heir && intent !== undefined && !intents.has(heir) && stateNames(heir).includes(intent)) {
                setIntent(heir, intent);
            }
            intents.delete(ctx);
        }
    }

    /** Re-derive the context from the selection. The ONLY place the context changes. */
    function recompute(): void {
        if (disposed) return;
        sweep();
        context = selection && !selection.transform.isDisposed() ? resolveContext(selection) : null;
        options.onChange?.();
    }

    // Re-derive on ANY Properties change: the derivation is a function of scene
    // DATA, and data can change without a selection event — the canonical case
    // is the lazy rig promotion, where the selection never moves but the
    // context must flip to the new rig and the demoted leaf's intent must
    // sweep. And on topology changes (reparent, rename, create, delete), since
    // the derivation walks the hierarchy. Coalesced into one microtask, so a
    // bundle of commits re-derives once, from settled data.
    let queued = false;
    const queueRecompute = (): void => {
        if (queued) return;
        queued = true;
        queueMicrotask(() => { queued = false; recompute(); });
    };
    const onBehaviorChanged = (event: Event): void => {
        if ((event as CustomEvent<BehaviorChangedDetail>).detail?.behaviorType === 'Properties') queueRecompute();
    };
    window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onBehaviorChanged);
    const disposeTopology = scene.onTopologyChanged(queueRecompute);

    /**
     * Run one states action on `target` and re-derive. A null outcome is a
     * refusal when the scene is in Play, else nothing to do.
     */
    async function edit(
        target: SceneObject | null,
        act: (target: SceneObject) => Promise<true | null>,
        { reposePages = false } = {},
    ): Promise<SessionResult> {
        if (!target) return null;
        if (stateEditRefused(target)) return { refused: PLAY_MODE_REFUSAL };
        const group = context;
        const done = await act(target);
        if (disposed) return done;
        // A rename or delete drops the state from every object it was showing
        // on, a page's choice included. The page's refs are healed by then, so
        // each deck re-applies its showing page across the group.
        if (reposePages && done === true && group && !group.transform.isDisposed()) {
            reposePageLooks(scene, new Set([group, ...groupDescendants(group)]));
        }
        recompute();
        if (done === null && stateEditRefused(target)) return { refused: PLAY_MODE_REFUSAL };
        return done;
    }

    return {
        update(next) {
            selection = next;
            recompute();
        },
        getContext: () => context,
        active() {
            if (!context) return null;
            const intended = getIntent(context);
            if (intended !== null && stateNames(context).includes(intended)) return intended;
            const showing = contextProps(context)?.getActiveState();
            return showing !== undefined && stateNames(context).includes(showing) ? showing : null;
        },
        select(name) {
            const ctx = context;
            if (!ctx) return;
            setIntent(ctx, name);
            if (name !== null) {
                switchState(ctx, name, true);
                return;
            }
            // Base picks Base on the context and clears the members' picks, so
            // each member shows its runtime slot: the page's choice, if any.
            for (const obj of [ctx, ...groupDescendants(ctx)]) {
                const props = contextProps(obj);
                if (!props) continue;
                props.setEditorState(obj === ctx ? null : undefined, false);
                notifyStateShown(obj);
            }
        },
        add: () => edit(context, async (ctx) => {
            const added = await addStateForSubtree(ctx);
            if (!added) return null;
            setIntent(added.owner, added.name);
            return true;
        }),
        rename: (from, to) => edit(context, async (ctx) => {
            // Read the intent before renaming: a recompute during the rename's
            // commits sweeps an intent whose state is gone, as `from` then is.
            const wasSelected = getIntent(ctx) === from;
            if (!await renameStateForSubtree(ctx, from, to)) return null;
            if (wasSelected) setIntent(ctx, to);
            return true;
        }, { reposePages: true }),
        remove: (name) => edit(context, async (ctx) => {
            // Drop the intent first, so a recompute during the deletion's
            // commits finds nothing to sweep; put it back if nothing was deleted.
            const wasSelected = getIntent(ctx) === name;
            if (wasSelected) setIntent(ctx, null);
            if (await deleteStateForSubtree(ctx, name)) return true;
            if (wasSelected) setIntent(ctx, name);
            return null;
        }, { reposePages: true }),
        duplicate: (name) => edit(context, async (ctx) => {
            const copy = await cloneStateForSubtree(ctx, name);
            if (copy === null) return null;
            setIntent(ctx, copy);
            return true;
        }),
        separate: () => edit(selection, async (subject) => (await createNestedStateGroup(subject) ? true : null)),
        rejoin: () => edit(selection, async (subject) => {
            // The selection's own group goes away: drop its intent first, so the
            // sweep doesn't hand it to the outer group, whose same-named state
            // is a different state the user never picked there.
            const intent = getIntent(subject);
            setIntent(subject, null);
            if (!await dissolveNestedStateGroup(subject)) {
                setIntent(subject, intent);
                return null;
            }
            // The member now coordinates with the group around it: show that
            // group's state on it.
            const outer = nearestStrictAncestorOwner(subject);
            const showing = outer ? getIntent(outer.sceneObject) ?? outer.getActiveState() : undefined;
            if (showing) applyStateToSubtree(subject, showing);
            return true;
        }),
        close() {
            intents.clear();
            if (scene.isPlayMode()) return { refused: PLAY_MODE_REFUSAL };
            for (const obj of scene.getSceneObjects()) {
                const props = contextProps(obj);
                if (props?.getEditorState() === undefined) continue;
                props.setEditorState(undefined, true);
                notifyStateShown(obj);
            }
            return true;
        },
        dispose() {
            disposed = true;
            window.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, onBehaviorChanged);
            disposeTopology();
            intents.clear();
        },
    };
}
